// src/pages/Checkout.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { orderAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiCreditCard, FiDollarSign, FiLock } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import API from '../services/api';

const Checkout = () => {
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');

  const loadCart = useCallback(() => {
    try {
      const raw = localStorage.getItem('cart') || '[]';
      const items = JSON.parse(raw);

      if (!Array.isArray(items) || items.length === 0) {
        toast.error('Votre panier est vide');
        navigate('/cart');
        return;
      }

      const normalized = items.map((it, idx) => {
        const dealId =
          it.dealId && typeof it.dealId === 'object'
            ? it.dealId._id || it.dealId.id
            : it.dealId || it._id || it.id;

        return {
          _localKey: it._localKey || `${dealId || 'x'}-${idx}`,
          dealId,
          quantity: Number(it.quantity) || 1,
          price: Number(it.price) || 0,
          title: it.title || (it.dealId && it.dealId.title) || 'Deal',
          image: it.image || (it.dealId && it.dealId.images && it.dealId.images[0]) || ''
        };
      });

      setCartItems(normalized);
      setSelectedKeys(normalized.map((it) => it._localKey));
    } catch (err) {
      console.error('loadCart parse error', err);
      toast.error('Impossible de charger le panier');
      navigate('/cart');
    }
  }, [navigate]);

  useEffect(() => {
    loadCart();
  }, [loadCart]);

  const selectedItems = cartItems.filter((item) => selectedKeys.includes(item._localKey));

  const subtotal = selectedItems.reduce(
    (acc, item) => acc + (Number(item.price) || 0) * (Number(item.quantity) || 0),
    0
  );
  const TAX_RATE = 0.2;
  const tax = subtotal * TAX_RATE;
  const total = subtotal + tax;

  const allSelected = cartItems.length > 0 && selectedKeys.length === cartItems.length;

  const toggleItemSelection = (key) => {
    setSelectedKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const toggleSelectAll = () => {
    if (allSelected) setSelectedKeys([]);
    else setSelectedKeys(cartItems.map((it) => it._localKey));
  };

  const submitGatewayForm = useCallback((paymentData) => {
    console.log('🔍 submitGatewayForm appelé avec:', paymentData);

    if (!paymentData || !paymentData.paymentUrl) {
      console.error('❌ paymentUrl manquante:', paymentData);
      toast.error('Erreur: URL de paiement manquante');
      return;
    }

    if (!paymentData.params || typeof paymentData.params !== 'object') {
      console.error('❌ params invalides:', paymentData);
      toast.error('Erreur: paramètres de paiement invalides');
      return;
    }

    try {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = paymentData.paymentUrl;
      form.style.display = 'none';

      Object.entries(paymentData.params).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value == null ? '' : String(value);
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      console.error('❌ Erreur lors de la création du formulaire:', error);
      toast.error('Erreur lors de la redirection vers le paiement');
    }
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!cartItems || cartItems.length === 0) {
        toast.error('Panier vide');
        return;
      }

      if (!selectedItems || selectedItems.length === 0) {
        toast.error('Sélectionnez au moins un article à payer');
        return;
      }

      if (!user || !user.email) {
        toast.error('Vous devez être connecté');
        navigate('/login');
        return;
      }

      // ✅ VÉRIFICATION TÉLÉPHONE OBLIGATOIRE
      if (!user.phone || user.phone.trim() === '') {
        toast.error('Veuillez ajouter votre numéro de téléphone dans votre profil avant de commander');
        navigate('/dashboard/profile');
        return;
      }

      setLoading(true);

      try {
        // 1) Créer commande
        const orderData = {
          items: selectedItems.map((item) => ({
            dealId: item.dealId,
            quantity: Number(item.quantity) || 1,
            price: Number(item.price) || 0
          })),
          paymentMethod
        };

        const orderResponse = await orderAPI.create(orderData);

        if (!orderResponse?.data?.success) {
          throw new Error(orderResponse?.data?.message || 'Erreur création commande');
        }

        const responseData = orderResponse.data.data;
        const order = responseData.order || responseData;
        const orderId = order._id || order.id;

        if (!orderId) {
          console.error('❌ Structure de réponse:', orderResponse.data);
          throw new Error('ID de commande manquant');
        }

        console.log('✅ Commande créée:', orderId);

        // MAJ panier local
        const remainingItems = cartItems.filter((item) => !selectedKeys.includes(item._localKey));
        if (remainingItems.length > 0) localStorage.setItem('cart', JSON.stringify(remainingItems));
        else localStorage.removeItem('cart');

        setCartItems(remainingItems);
        setSelectedKeys(remainingItems.map((it) => it._localKey));

        // 2) Paiement
        if (paymentMethod === 'card') {
          toast.info('Redirection vers la page de paiement sécurisé...');

          const paymentResponse = await API.post('/payment/initiate', { orderId });

          if (!paymentResponse?.data?.success) {
            throw new Error(paymentResponse?.data?.message || 'Erreur initialisation paiement');
          }

          const paymentData = paymentResponse.data.data;

          if (!paymentData?.paymentUrl || !paymentData?.params) {
            throw new Error('Données de paiement invalides reçues du serveur');
          }

          localStorage.setItem('pendingOrderId', orderId);

          setTimeout(() => submitGatewayForm(paymentData), 300);
          return;
        }

        // Paiement différé
        toast.success('Commande créée ! Paiement en attente.');
        navigate('/dashboard/orders');
      } catch (error) {
        console.error('❌ Erreur checkout:', error);
        const errorMessage =
          error?.response?.data?.message || error.message || 'Erreur lors du paiement';
        toast.error(errorMessage);
        setLoading(false);
      }
    },
    [cartItems, selectedItems, paymentMethod, user, navigate, submitGatewayForm, selectedKeys]
  );

  const submitDisabled = loading || selectedItems.length === 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold mb-8">Finaliser la commande</h1>

      <form onSubmit={handleSubmit}>
        <div className="grid md:grid-cols-3 gap-8">
          {/* Formulaire */}
          <div className="md:col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold mb-4">Informations client</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Nom complet</label>
                  <input
                    type="text"
                    value={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Email</label>
                  <input
                    type="email"
                    value={user?.email ?? ''}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Les vouchers seront envoyés à cette adresse
                  </p>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Téléphone</label>
                  <input
                    type="tel"
                    value={user?.phone ?? ''}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                  />
                  {/* ✅ MESSAGE SI PAS DE TÉLÉPHONE */}
                  {(!user?.phone || user.phone.trim() === '') && (
                    <p className="text-sm text-red-600 mt-1">
                      ⚠️ Ajoutez votre téléphone dans votre profil
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Méthode paiement */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold mb-4">Méthode de paiement</h2>

              <div className="space-y-4">
                <label
                  className={`flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition ${
                    paymentMethod === 'card'
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-gray-200'
                  }`}
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="card"
                    checked={paymentMethod === 'card'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="mr-4"
                  />
                  <FiCreditCard className="text-primary-600 mr-3" size={24} />
                  <div className="flex-1">
                    <div className="font-semibold">Carte bancaire (Payzone)</div>
                    <div className="text-sm text-gray-600">
                      Paiement sécurisé via Payzone (redirection)
                    </div>
                  </div>
                  <FiLock className="text-gray-400" />
                </label>

                <label
                  className={`flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition ${
                    paymentMethod === 'cash'
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-gray-200'
                  }`}
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="cash"
                    checked={paymentMethod === 'cash'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="mr-4"
                  />
                  <FiDollarSign className="text-primary-600 mr-3" size={24} />
                  <div className="flex-1">
                    <div className="font-semibold">Paiement différé</div>
                    <div className="text-sm text-gray-600">
                      Payez plus tard (validation requise)
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Résumé */}
          <div>
            <div className="bg-white rounded-lg shadow p-6 sticky top-24">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Résumé</h2>
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  className="text-sm text-primary-600 hover:text-primary-700 font-semibold"
                >
                  {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                </button>
              </div>

              <p className="text-sm text-gray-500 mb-2">
                {selectedItems.length} article{selectedItems.length > 1 ? 's' : ''} sélectionné
                {selectedItems.length > 1 ? 's' : ''} {' / '}
                {cartItems.length} au total
              </p>

              <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                {cartItems.map((item) => (
                  <div key={item._localKey} className="flex space-x-3 items-center">
                    <input
                      type="checkbox"
                      checked={selectedKeys.includes(item._localKey)}
                      onChange={() => toggleItemSelection(item._localKey)}
                      className="mr-2"
                    />
                    <img
                      src={item.image || '/placeholder.png'}
                      alt={item.title}
                      className="w-16 h-16 object-cover rounded"
                    />
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm">{item.title}</h4>
                      <p className="text-sm text-gray-600">
                        {item.quantity} x {Number(item.price).toFixed(2)} MAD
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4 space-y-2 mb-4">
                <div className="flex justify-between text-gray-600">
                  <span>Sous-total</span>
                  <span>{subtotal.toFixed(2)} MAD</span>
                </div>

                <div className="flex justify-between text-gray-600">
                  <span>TVA (20%)</span>
                  <span>{tax.toFixed(2)} MAD</span>
                </div>

                <div className="border-t pt-2 flex justify-between font-bold text-xl">
                  <span>Total</span>
                  <span className="text-primary-600">{total.toFixed(2)} MAD</span>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitDisabled}
                className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition"
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Traitement...
                  </>
                ) : (
                  <>
                    <FiLock className="mr-2" />
                    <span>Payer {total.toFixed(2)} MAD</span>
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500 text-center mt-3">
                <FiLock className="inline mr-1" />
                Paiement 100% sécurisé
              </p>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Checkout;